<template>
  <div v-if="pathway" class="view-pathway">
    <Modal
      v-model="showModal"
      :footer="false"
      :header="false"
      :backdrop="false"
      data-test="team-modal-animation"
    >
      <template #default>
        <div style="margin-inline: auto" class="text-center">
          <h3>Good luck on your journey!</h3>
        </div>
        <div class="animation-container">
          <LottieProgressBar ref="lottieProgressBar" width="75%" />
        </div>
      </template>
    </Modal>
    <div class="view-pathway-description">
      <div class="pathway-header">
        <div class="pathway-header-text">
          <h2 data-test="view-pathway-title">
            <b>Pathway: </b>{{ cms.asText(pathway.data.title) }}
          </h2>
          <div
            v-html="cms.asHtml(pathway.data.short_description)"
            data-test="view-short-desc"
          ></div>
        </div>
        <div class="pathway-header-image">
          <img
            :src="pathway.data.title_image.url"
            :alt="cms.asText(pathway.data.title)"
            data-test="view-pathway-img"
          />
        </div>
      </div>
      <div class="pathway-section">
        <p v-html="cms.asHtml(pathway.data.description)" data-test="view-pathway-desc"></p>
      </div>
      <div>
        <ul class="practicalities-list" data-test="view-pathway-practicalities">
          <li
            v-for="(p, index) in pathway.data.practicalities"
            :key="`practicalities` + index"
            class="practicalities-list-item"
          >
            <div class="practicality-title">
              <img src="../../assets/light-bulb-icon.svg" :alt="`benefit` + (index + 1)" />
              <p>{{ cms.asText(p.practicality_title) }}</p>
            </div>
            <p class="practicality-desc-item" v-html="cms.asHtml(p.practicality_description)"></p>
          </li>
        </ul>
      </div>
    </div>
    <div class="btn-container">
      <button
        @click="saveTeamPathway"
        class="btn btn-primary"
        data-test="pathway-save"
        type="button"
      >
        Go!
      </button>
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import axios from "axios";
import { debug as d } from "debug";
import { Modal } from "uiv";
import { onBeforeMount, ref, computed } from "vue";
import { useRoute } from "vue-router";
import cms from "../../services/cms/cms";
import { CmsTeamGoal, CmsTeamPathway } from "../../services/cms/cms-type-definitions";
import { TeamPathwayApiModel } from "../../api-models";
import LoadingPage from "../../views/loading-page.vue";
import { Analytics } from "../../services/analytics/analytics";
import { csContext } from "../../services/cs-context";
import LottieProgressBar from "../lottie-progress-bar.vue";

const debug = d("cs.PackagesPage");

const route = useRoute();
const asaccredited = computed(() => route.query.asaccredited as string);

const pathwayUid = ref("");
const teamId = ref("");
const outcomeUid = ref("");
const pathway = ref<CmsTeamPathway | null>(null);
const outcome = ref<CmsTeamGoal | null>(null);
const expanded = ref(false);
const selectedPathwayApiModel = ref<TeamPathwayApiModel | null>(null);
const showModal = ref(false);

onBeforeMount(async () => {
  pathwayUid.value = route.params.pathwayUid as string;
  outcomeUid.value = route.params.outcomeUid as string;
  teamId.value = route.params.id as string;
  pathway.value = await cms.getByUid("cms-pathway", pathwayUid.value);
  outcome.value = await cms.getByUid("cms-goal", outcomeUid.value);

  Analytics.action("Viewed Team Pathway Details", "TeamPathwayActions", {
    outcomeUid: outcomeUid.value,
    outcomeTitle: cms.asText(outcome.value.data.full_title),
    pathwayId: pathway.value?.id,
    pathwayUid: pathwayUid.value,
    pathwayName: pathway.value?.data?.title[0].text,
    pathwayTitle: cms.asText(pathway.value.data.title),
  });
});

function toggle(): void {
  expanded.value = !expanded.value;
}

const emit = defineEmits(["hide-stepper"]);
const lottieProgressBar = ref();

function saveTeamPathway(): void {
  if (!pathway.value || !outcome.value) {
    throw new Error("Need to have pathway & outcome in place to save");
  }

  if (!pathway.value.uid || !outcome.value.uid) {
    throw new Error("Pathway and outcome need to have UIDs set");
  }

  selectedPathwayApiModel.value = {
    triggeringUserId: csContext.user.id,
    cmsPathwayId: pathway.value.id,
    cmsPathwayUid: pathway.value.uid,
    cmsPathwayTitle: cms.asText(pathway.value.data.title),
    cmsOutcomeTitle: cms.asText(outcome.value.data.full_title),
    cmsOutcomeUid: outcome.value.uid,
    cmsOutcomeId: outcome.value.id,
    isCurrent: true,
    completed: false,
  };
  axios.post(`/api/teampathway/${teamId.value}`, selectedPathwayApiModel.value).then((response) => {
    debug("Team pathway saved", response);
    emit("hide-stepper");
    showModal.value = true;
    (lottieProgressBar.value as any).playAnimation();
    setTimeout(() => {
      window.location.href = asaccredited.value === 'true'
        ? `/myorganisation/group/details/${teamId.value}`
        : `/home?team=${teamId.value}`;
    }, 3000);
  });
}
</script>

<style scoped lang="less">
@import "../../styles/outcomes/pathway-summary";
</style>

<style lang="less">
@import "../../styles/outcomes/prismic-pathway-summary";
</style>
